<template>
  <div>
    <b-modal
      v-model="modalVisible"
      @hidden="$emit('close')"
      title="Device Configuration"
      size="lg"
    >
    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2">Loading device configuration...</p>
    </div>
    
    <div v-else-if="error" class="alert alert-danger">
      <strong>Error:</strong> {{ error.message }}
    </div>
    
    <div v-else-if="deviceConfig">
      <b-form @submit.prevent="saveConfiguration">
        <!-- Device Info -->
        <b-card class="mb-3">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="card-title mb-0">Device Information</h5>
            <b-button type="button" variant="outline-primary" size="sm" :disabled="savingDevice" @click="saveDeviceFields">
              <span v-if="savingDevice"><b-spinner small></b-spinner> Saving...</span>
              <span v-else><i class="fa fa-save"></i> Save Device</span>
            </b-button>
          </div>
          <b-row>
            <b-col md="6">
              <strong>Name:</strong> {{ device.name }}
            </b-col>
            <b-col md="6">
              <strong>Serial:</strong> {{ device.serial }}
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col md="6">
              <strong>Site:</strong> {{ (device.site && device.site.name) || 'N/A' }}
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col md="4">
              <b-form-group label="Device Type" label-for="dev-type" class="mb-0">
                <b-form-select
                  id="dev-type"
                  v-model="deviceForm.type"
                  :options="deviceTypeOptions"
                ></b-form-select>
                <small v-if="deviceTypeChanged" class="text-warning d-block mt-1">
                  <i class="fa fa-exclamation-triangle mr-1"></i>
                  Readings are stored per type. Changing <code>{{ device.type }}</code> to
                  <code>{{ deviceForm.type }}</code> points this device at a different sensor
                  table, so its existing history will no longer appear.
                </small>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Site" label-for="dev-site" class="mb-0">
                <b-form-select
                  id="dev-site"
                  v-model="deviceForm.siteId"
                  :options="siteOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Elevation (ft)" label-for="dev-elevation" class="mb-0">
                <b-form-input
                  id="dev-elevation"
                  v-model.number="deviceForm.elevation"
                  type="number"
                  step="0.01"
                  placeholder="e.g. 4250.00"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4" class="d-flex align-items-end">
              <b-form-group label="Device Enabled" class="mb-0">
                <b-form-checkbox v-model="deviceForm.enabled" switch size="lg">
                  {{ deviceForm.enabled ? 'Enabled' : 'Disabled' }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col md="4">
              <b-form-group label="Latitude" label-for="dev-latitude" class="mb-0">
                <b-form-input
                  id="dev-latitude"
                  v-model.number="deviceForm.latitude"
                  type="number"
                  step="0.000001"
                  min="-90"
                  max="90"
                  placeholder="e.g. 37.774929"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Longitude" label-for="dev-longitude" class="mb-0">
                <b-form-input
                  id="dev-longitude"
                  v-model.number="deviceForm.longitude"
                  type="number"
                  step="0.000001"
                  min="-180"
                  max="180"
                  placeholder="e.g. -122.419416"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        
        <!-- Configuration Form -->
        <b-card>
          <h5 class="card-title">Configuration Settings</h5>
          
          <b-row>
            <b-col md="6">
              <b-form-group label-for="type">
                <template #label>
                  Config Type
                  <i id="config-type-info" class="fa fa-info-circle text-muted ml-1" style="cursor:pointer;"></i>
                  <b-tooltip target="config-type-info" placement="right" triggers="hover">
                    The type sent to the device firmware in its config payload. Normally matches
                    the device type above; that one is what the dashboard and API route on.
                  </b-tooltip>
                </template>
                <b-form-select
                  id="type"
                  v-model="form.type"
                  :state="validateField('type')"
                  :options="deviceTypeOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
            
            <b-col md="6">
              <b-form-group label="Subtype" label-for="subtype">
                <b-form-input
                  id="subtype"
                  v-model="form.subtype"
                  placeholder="Enter device subtype"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          
          <b-row>
            <b-col md="6">
              <b-form-group label="Company" label-for="company">
                <b-form-select
                  id="company"
                  v-model="form.company"
                  :options="companyOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
            
            <b-col md="6">
              <b-form-group label-for="loopInt">
                <template #label>
                  Loop Interval (s)
                  <i
                    id="loopInt-info"
                    class="fa fa-info-circle text-muted ml-1"
                    style="cursor:pointer;"
                  ></i>
                  <b-tooltip target="loopInt-info" placement="right" triggers="hover">
                    <span v-if="form.loopInt > 300">
                      <strong>Heavy Sleep</strong> — device will fully disconnect and requires a Particle Cloud reconnect on wake. Commands are not responsive.
                    </span>
                    <span v-else-if="form.loopInt > 0">
                      <strong>Network Sleep</strong> — device stays connected to the network, allowing responsive commands to be sent.
                    </span>
                    <span v-else>
                      Set loop interval to determine sleep mode. &lt;300s = network sleep, &gt;300s = heavy sleep.
                    </span>
                  </b-tooltip>
                </template>
                <b-form-input
                  id="loopInt"
                  v-model.number="form.loopInt"
                  type="number"
                  min="0"
                  :state="validateField('loopInt')"
                  placeholder="Enter loop interval"
                ></b-form-input>
                <small v-if="form.loopInt > 0" class="text-muted">{{ formatSeconds(form.loopInt) }}</small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label-for="publishInt">
                <template #label>
                  Publish Interval (s)
                  <i
                    id="publishInt-info"
                    class="fa fa-info-circle text-muted ml-1"
                    style="cursor:pointer;"
                  ></i>
                  <b-tooltip target="publishInt-info" placement="right" triggers="hover">
                    How frequently the device sends sensor readings to the dashboard. A shorter interval gives more up-to-date data but increases data usage and battery consumption.
                  </b-tooltip>
                </template>
                <b-form-input
                  id="publishInt"
                  v-model.number="form.publishInt"
                  type="number"
                  min="0"
                  :state="validateField('publishInt')"
                  placeholder="Enter publish interval"
                ></b-form-input>
                <small v-if="form.publishInt > 0" class="text-muted">{{ formatSeconds(form.publishInt) }}</small>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label-for="keepAliveMode">
                <template #label>
                  Keep Alive
                  <i
                    id="keepAlive-info"
                    class="fa fa-info-circle text-muted ml-1"
                    style="cursor:pointer;"
                  ></i>
                  <b-tooltip target="keepAlive-info" placement="right" triggers="hover">
                    When using a Particle internal SIM, the keep-alive ping is managed automatically by the Particle network and this value is ignored. Only applies to external SIM providers (e.g. Hologram, Verizon) where a manual ping interval is required to maintain the data session.
                  </b-tooltip>
                </template>
                <b-form-select
                  id="keepAliveMode"
                  v-model="keepAliveMode"
                  :options="keepAliveOptions"
                  @change="onKeepAliveModeChange"
                ></b-form-select>
                <b-form-input
                  v-if="keepAliveMode === 'custom'"
                  v-model.number="form.keepAlive"
                  type="number"
                  min="0"
                  class="mt-2"
                  placeholder="Enter keep alive (s)"
                ></b-form-input>
                <small v-if="form.keepAlive > 0" class="text-muted">{{ form.keepAlive }}s &mdash; {{ formatSeconds(form.keepAlive) }}</small>
              </b-form-group>
            </b-col>
          </b-row>
          
          <b-row>
            <b-col md="6">
              <b-form-group label="Stay Awake" label-for="stayAwake">
                <b-form-checkbox
                  id="stayAwake"
                  v-model="form.stayAwake"
                  :value="true"
                  :unchecked-value="false"
                  switch
                >
                  Keep device awake
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Debug Mode" label-for="debug">
                <b-form-checkbox
                  id="debug"
                  v-model="form.debug"
                  :value="true"
                  :unchecked-value="false"
                  switch
                >
                  Enable debug output
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label-for="sleepType">
                <template #label>
                  Sleep Type
                  <i
                    id="sleepType-info"
                    class="fa fa-info-circle text-muted ml-1"
                    style="cursor:pointer;"
                  ></i>
                  <b-tooltip target="sleepType-info" placement="right" triggers="hover">
                    <strong>Stop / Network</strong> — device stays connected to the Particle network during sleep, allowing responsive commands.<br><br>
                    <strong>ULP (Ultra Low Power)</strong> — device fully disconnects from the network during sleep. Lowest power draw but commands are not delivered until the device wakes and reconnects.
                  </b-tooltip>
                </template>
                <b-form-select
                  id="sleepType"
                  v-model="form.sleepType"
                  :options="sleepTypeOptions"
                ></b-form-select>
                <small v-if="sleepTypeControlledBy.length" class="text-warning d-block mt-1">
                  <i class="fa fa-bolt mr-1"></i>
                  Controlled by an alert action on
                  <strong>{{ sleepTypeControlledBy.map(function (r) { return r.label }).join(', ') }}</strong>.
                  While that rule's condition holds, the evaluator re-asserts
                  <code>sleepType</code> every 30s and a change made here will be reverted.
                  Disable the rule's action first if you need to set it by hand.
                </small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="BME Calibration" label-for="bmeCal">
                <b-form-input
                  id="bmeCal"
                  v-model.number="form.bmeCal"
                  type="number"
                  step="0.01"
                  placeholder="Enter BME calibration value"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          
          <b-row>
            <b-col md="6">
              <b-form-group label="Min Charge Temperature (°C)" label-for="minChgTmp">
                <b-form-input
                  id="minChgTmp"
                  v-model.number="form.minChgTmp"
                  type="number"
                  step="0.1"
                  placeholder="Enter minimum charge temperature"
                ></b-form-input>
              </b-form-group>
            </b-col>
            
            <b-col md="6">
              <b-form-group label="Voltage Limit (V)" label-for="voltLimit">
                <b-form-input
                  id="voltLimit"
                  v-model.number="form.voltLimit"
                  type="number"
                  min="0"
                  placeholder="Enter voltage limit"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          
          <b-row>
            <b-col md="6">
              <b-form-group label="Current Limit (A)" label-for="currentLimit">
                <b-form-input
                  id="currentLimit"
                  v-model.number="form.currentLimit"
                  type="number"
                  min="0"
                  placeholder="Enter current limit"
                ></b-form-input>
              </b-form-group>
            </b-col>
            
            <b-col md="6">
              <b-form-group label="Charge Voltage (V)" label-for="chargeVolt">
                <b-form-input
                  id="chargeVolt"
                  v-model.number="form.chargeVolt"
                  type="number"
                  min="0"
                  placeholder="Enter charge voltage"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          
          <b-row>
            <b-col md="6">
              <b-form-group label="ADC Minimum" label-for="adcMin">
                <b-form-input
                  id="adcMin"
                  v-model.number="form.adcMin"
                  type="number"
                  min="0"
                  placeholder="Enter ADC minimum value"
                ></b-form-input>
              </b-form-group>
            </b-col>
            
            <b-col md="6">
              <b-form-group label="ADC Maximum" label-for="adcMax">
                <b-form-input
                  id="adcMax"
                  v-model.number="form.adcMax"
                  type="number"
                  min="0"
                  placeholder="Enter ADC maximum value"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        
        <!-- Sensors Section -->
        <b-card class="mt-3">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="card-title mb-0">Device Sensors</h5>
            <b-button variant="success" size="sm" @click="showAddSensorModal = true">
              <i class="fa fa-plus"></i> Add Sensor
            </b-button>
          </div>
          
          <div v-if="!deviceConfig.sensors || deviceConfig.sensors.length === 0" class="text-center text-muted py-3">
            <i class="fa fa-microchip fa-2x mb-2"></i>
            <p>No sensors configured for this device.</p>
            <p class="small">Click "Add Sensor" to configure device sensors.</p>
          </div>
          
          <b-table
            v-else
            :items="deviceConfig.sensors"
            :fields="sensorFields"
            striped
            small
            responsive
            hover
          >
            <template #cell(cal)="data">
              <span>{{ data.item.cal != null ? parseFloat(data.item.cal).toFixed(4) : '—' }}</span>
            </template>
            
            <template #cell(actions)="data">
              <b-dropdown size="sm" variant="outline-secondary" right no-caret boundary="viewport">
                <template #button-content>
                  <i class="fa fa-ellipsis-v"></i>
                </template>
                <b-dropdown-item @click="editSensor(data.item)">
                  <i class="fa fa-edit mr-1"></i> Edit
                </b-dropdown-item>
                <b-dropdown-item @click="calibrateSensor(data.item)">
                  <i class="fa fa-balance-scale mr-1"></i> Calibrate
                </b-dropdown-item>
                <b-dropdown-item variant="danger" @click="deleteSensor(data.item)">
                  <i class="fa fa-trash mr-1"></i> Delete
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </b-card>
      </b-form>
    </div>
    
    <div v-else>
      <div class="text-center text-muted mb-4">
        <i class="fa fa-microchip fa-3x mb-3"></i>
        <h5>No Configuration Found</h5>
        <p>This device hasn't been configured yet.</p>
        <b-button variant="primary" @click="createNewConfiguration">
          <i class="fa fa-plus"></i> Create Configuration
        </b-button>
      </div>
    </div>
    
    <!-- Custom Footer -->
    <template #modal-footer>
      <b-button variant="secondary" @click="$emit('close')">
        Cancel
      </b-button>
      <b-button
        v-if="deviceConfig || (!loading && !error)"
        variant="primary"
        @click="saveConfiguration"
        :disabled="loading || saving || !deviceConfig"
      >
        Save Changes
      </b-button>
    </template>
    </b-modal>
    
    <!-- Save Progress Dialog -->
    <b-modal
      v-model="progressModal.show"
      title="Saving Configuration"
      :no-close-on-backdrop="!progressModal.done"
      :no-close-on-esc="!progressModal.done"
      hide-header-close
      centered
      size="sm"
    >
      <div class="text-center py-2">
        <template v-if="!progressModal.done">
          <b-spinner variant="primary" style="width:3rem;height:3rem;" class="mb-3"></b-spinner>
          <p class="mb-3">{{ progressModal.stage }}</p>
          <b-progress :value="progressModal.value" :max="100" animated striped height="8px"></b-progress>
        </template>
        <template v-else-if="progressModal.variant === 'success'">
          <i class="fa fa-check-circle fa-3x text-success mb-3"></i>
          <p class="mb-0 font-weight-bold">Configuration saved successfully.</p>
        </template>
        <template v-else>
          <i class="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <p class="font-weight-bold mb-2">{{ progressModal.stage }}</p>
          <p class="text-muted small mb-0">{{ progressModal.warning }}</p>
        </template>
      </div>
      <template #modal-footer>
        <b-button
          v-if="progressModal.done && progressModal.variant !== 'success'"
          variant="secondary"
          @click="progressModal.show = false"
        >
          Close
        </b-button>
        <span v-else></span>
      </template>
    </b-modal>

    <!-- Add Sensor Modal -->
  <b-modal
    v-model="showAddSensorModal"
    title="Add Device Sensor"
    @ok="addNewSensor"
    @cancel="cancelAddSensor"
    @hidden="cancelAddSensor"
    ok-title="Add Sensor"
    cancel-title="Cancel"
    :ok-disabled="addingSensor"
    :cancel-disabled="addingSensor"
    :no-close-on-backdrop="addingSensor"
    :no-close-on-esc="addingSensor"
    :hide-header-close="addingSensor"
  >
    <b-form @submit.prevent="addNewSensor">
      <b-row>
        <b-col md="6">
          <b-form-group label="Sensor Label" label-for="sensor-label">
            <b-form-input
              id="sensor-label"
              v-model="newSensor.label"
              required
              placeholder="Enter sensor label"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Sensor Type" label-for="sensor-type">
            <b-form-select
              id="sensor-type"
              v-model="newSensor.type"
              :options="sensorTypeOptions"
              required
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      
      <b-row>
        <b-col md="6">
          <b-form-group label="Unit" label-for="sensor-unit">
            <b-form-input
              id="sensor-unit"
              v-model="newSensor.unit"
              placeholder="e.g., °C, m, %"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Interface" label-for="sensor-iface">
            <b-form-select
              id="sensor-iface"
              v-model="newSensor.iface"
              :options="interfaceOptions"
              required
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      
      <b-row>
        <b-col md="4">
          <b-form-group label="Channel" label-for="sensor-channel">
            <b-form-input
              id="sensor-channel"
              v-model.number="newSensor.channel"
              type="number"
              min="0"
              required
              placeholder="0"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Min Value" label-for="sensor-min">
            <b-form-input
              id="sensor-min"
              v-model.number="newSensor.min"
              type="number"
              required
              placeholder="0"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Max Value" label-for="sensor-max">
            <b-form-input
              id="sensor-max"
              v-model.number="newSensor.max"
              type="number"
              required
              placeholder="100"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      
      <b-row>
        <b-col md="6">
          <b-form-group label="Calibration" label-for="sensor-cal">
            <b-form-input
              id="sensor-cal"
              v-model.number="newSensor.cal"
              type="number"
              step="0.001"
              placeholder="1.000"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Number" label-for="sensor-nr">
            <b-form-input
              id="sensor-nr"
              v-model.number="newSensor.nr"
              type="number"
              min="1"
              placeholder="1"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
    </b-modal>
  </div>
</template>

<script>
import { DEVICE_CONFIG_QUERY, UPDATE_DEVICE_CONFIG_MUTATION, CREATE_DEVICE_CONFIG_MUTATION, CREATE_DEVICE_SENSOR_MUTATION, DELETE_DEVICE_SENSOR_MUTATION, COMPANIES_QUERY, UPDATE_DEVICE_MUTATION, SITES_QUERY, RULES_QUERY } from '@/constants/graphql'
import { getAccessToken } from '@/lib/auth'
import { DEVICE_TYPE_OPTIONS } from '@/constants/data'

const API_BASE = (process.env.VUE_APP_API_ENDPOINT || 'https://apiv2.leaf.ai/graphql').replace('/graphql', '')

export default {
  name: 'DeviceConfigModal',
  props: {
    device: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  created () {
    // eslint-disable-next-line no-console
    console.log('DeviceConfigModal created with device:', this.device, 'visible:', this.visible)
  },
  mounted () {
    // eslint-disable-next-line no-console
    console.log('DeviceConfigModal mounted with device:', this.device, 'visible:', this.visible)
    // If modal is already visible when mounted, load config immediately
    if (this.visible && this.device) {
      // eslint-disable-next-line no-console
      console.log('Modal is visible on mount, loading config...')
      this.loadDeviceConfig()
    }
  },
  data () {
    return {
      loading: false,
      error: null,
      deviceConfig: null,
      saving: false,
      savingDevice: false,
      progressModal: {
        show: false,
        done: false,
        value: 0,
        stage: '',
        variant: 'primary',
        warning: null
      },
      keepAliveMode: 'verizon',
      keepAliveOptions: [
        { value: 'verizon', text: 'Verizon (1380s)' },
        { value: 'hologram', text: 'Hologram (60s)' },
        { value: 'custom', text: 'Custom' }
      ],
      deviceForm: {
        type: '',
        elevation: null,
        enabled: true,
        siteId: null,
        latitude: null,
        longitude: null
      },
      form: {
        type: 'well',
        subtype: '',
        company: '',
        loopInt: 1800,
        publishInt: 7200,
        keepAlive: 1380,
        stayAwake: false,
        sleepType: null,
        debug: false,
        bmeCal: -2,
        minChgTmp: 25,
        voltLimit: 4520,
        currentLimit: 900,
        chargeVolt: 4208,
        adcMin: 6400,
        adcMax: 32000
      },
      deviceTypeOptions: DEVICE_TYPE_OPTIONS,
      sleepTypeOptions: [
        { value: null, text: 'Default (Stop / Network)' },
        { value: 'stop', text: 'Stop / Network — stays connected, responsive commands' },
        { value: 'ulp', text: 'ULP — ultra low power, disconnects from network' }
      ],
      showAddSensorModal: false,
      newSensor: {
        label: '',
        type: '',
        unit: '',
        iface: '',
        channel: 0,
        min: 0,
        max: 100,
        cal: 1.0,
        nr: 1
      },
      sensorTypeOptions: [
        { value: '', text: 'Select sensor type...' },
        { value: 'temperature', text: 'Temperature' },
        { value: 'pressure', text: 'Pressure' },
        { value: 'humidity', text: 'Humidity' },
        { value: 'flow', text: 'Flow' },
        { value: 'level', text: 'Level' },
        { value: 'ph', text: 'pH' },
        { value: 'conductivity', text: 'Conductivity' },
        { value: 'turbidity', text: 'Turbidity' },
        { value: 'dissolved_oxygen', text: 'Dissolved Oxygen' },
        { value: 'voltage', text: 'Voltage' },
        { value: 'current', text: 'Current' }
      ],
      interfaceOptions: [
        { value: '', text: 'Select interface...' },
        { value: 'currentLoop', text: 'Current Loop' },
        { value: 'analog', text: 'Analog' },
        { value: 'uart', text: 'UART' },
        { value: 'ezo', text: 'EZO' }
      ],
      addingSensor: false,
      sensorFields: [
        { key: 'label', label: 'Label' },
        { key: 'type', label: 'Type', thClass: 'd-none d-sm-table-cell', tdClass: 'd-none d-sm-table-cell' },
        { key: 'unit', label: 'Unit', thClass: 'd-none d-md-table-cell', tdClass: 'd-none d-md-table-cell' },
        { key: 'channel', label: 'Ch', thClass: 'd-none d-md-table-cell', tdClass: 'd-none d-md-table-cell' },
        { key: 'iface', label: 'Interface', thClass: 'd-none d-lg-table-cell', tdClass: 'd-none d-lg-table-cell' },
        { key: 'cal', label: 'Cal', thClass: 'd-none d-lg-table-cell', tdClass: 'd-none d-lg-table-cell' },
        { key: 'actions', label: '', class: 'text-right' }
      ]
    }
  },
  apollo: {
    companies: {
      query: COMPANIES_QUERY
    },
    sites: {
      query: SITES_QUERY
    },
    // Only used to warn when an alert action owns this device's sleepType. Failing to
    // load must not break the config form, so errors are swallowed.
    rules: {
      query: RULES_QUERY,
      error () { /* the warning simply will not render */ }
    }
  },
  computed: {
    companyOptions () {
      if (!this.companies) return [{ value: '', text: 'Select company...' }]
      return [
        { value: '', text: 'Select company...' },
        ...this.companies.map(c => ({ value: c.key, text: c.name }))
      ]
    },
    siteOptions () {
      if (!this.sites) return [{ value: null, text: 'Select site...' }]
      return [
        { value: null, text: 'Select site...' },
        ...this.sites.map(s => ({ value: s.id, text: s.name }))
      ]
    },
    modalVisible: {
      get () {
        return this.visible
      },
      set (value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    // Enabled rules whose enabled setSleepType action targets this device. The evaluator
    // re-asserts the value on every pass the condition holds, so a manual edit here would
    // be silently reverted within ~30s.
    sleepTypeControlledBy () {
      var device = this.device
      if (!device || !this.rules) return []
      return this.rules.filter(function (rule) {
        if (rule.enabled === false) return false
        var hasAction = (rule.actions || []).some(function (a) {
          return a.type === 'setSleepType' && a.enabled !== false
        })
        if (!hasAction) return false
        return (rule.devices || []).some(function (d) {
          return d.id === device.id || d.serial === device.serial
        })
      })
    },
    deviceTypeChanged () {
      return !!(this.device && this.deviceForm.type && this.deviceForm.type !== this.device.type)
    },
    isFormValid () {
      const valid = this.form.type && this.form.loopInt >= 0 && this.form.publishInt >= 0
      // eslint-disable-next-line no-console
      console.log('Form validation:', { 
        type: this.form.type, 
        loopInt: this.form.loopInt, 
        publishInt: this.form.publishInt, 
        valid,
        deviceConfig: !!this.deviceConfig 
      })
      return valid
    }
  },
  watch: {
    visible (newVal) {
      // eslint-disable-next-line no-console
      console.log('Modal visibility changed:', newVal, 'Device:', this.device)
      if (newVal && this.device) {
        // eslint-disable-next-line no-console
        console.log('Calling loadDeviceConfig...')
        this.loadDeviceConfig()
      }
    }
  },
  methods: {
    async loadDeviceConfig () {
      // eslint-disable-next-line no-console
      console.log('loadDeviceConfig called for device:', this.device.serial)
      this.loading = true
      this.error = null
      
      try {
        // eslint-disable-next-line no-console
        console.log('Executing DEVICE_CONFIG_QUERY with serial:', this.device.serial)
        const result = await this.$apollo.query({
          query: DEVICE_CONFIG_QUERY,
          variables: { serial: this.device.serial },
          fetchPolicy: 'network-only'
        })
        
        // eslint-disable-next-line no-console
        console.log('Query result:', result)
        this.deviceConfig = result.data.deviceConfigBySerial
        // eslint-disable-next-line no-console
        console.log('Device config loaded:', this.deviceConfig)
        
        // Always populate device-level fields regardless of config
        this.deviceForm.type = this.device.type || ''
        this.deviceForm.elevation = this.device.elevation != null ? this.device.elevation : null
        this.deviceForm.enabled = this.device.enabled != null ? this.device.enabled : true
        this.deviceForm.siteId = (this.device.site && this.device.site.id) ? this.device.site.id : null
        this.deviceForm.latitude = this.device.latitude != null ? this.device.latitude : null
        this.deviceForm.longitude = this.device.longitude != null ? this.device.longitude : null

        if (this.deviceConfig) {
          // Populate form with existing configuration
          Object.keys(this.form).forEach(key => {
            if (this.deviceConfig[key] !== null && this.deviceConfig[key] !== undefined) {
              this.form[key] = this.deviceConfig[key]
            }
          })
          // Set keepAlive mode based on loaded value
          if (this.deviceConfig.keepAlive === 1380) {
            this.keepAliveMode = 'verizon'
          } else if (this.deviceConfig.keepAlive === 60) {
            this.keepAliveMode = 'hologram'
          } else if (this.deviceConfig.keepAlive != null) {
            this.keepAliveMode = 'custom'
          }
          // eslint-disable-next-line no-console
          console.log('Form populated:', this.form)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading device config:', error)
        this.error = error
      } finally {
        this.loading = false
      }
    },
    
    formatSeconds (s) {
      if (!s || s <= 0) return ''
      const totalMinutes = Math.floor(s / 60)
      const seconds = s % 60
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      const parts = []
      if (hours > 0) parts.push(hours + 'h')
      if (minutes > 0) parts.push(minutes + 'm')
      if (seconds > 0) parts.push(seconds + 's')
      return parts.join(' ')
    },

    onKeepAliveModeChange (mode) {
      if (mode === 'verizon') {
        this.form.keepAlive = 1380
      } else if (mode === 'hologram') {
        this.form.keepAlive = 60
      }
      // 'custom' leaves form.keepAlive as-is for the user to edit
    },

    validateField (fieldName) {
      switch (fieldName) {
        case 'type':
          return this.form.type ? true : false
        case 'loopInt':
        case 'publishInt':
          return this.form[fieldName] >= 0 ? true : false
        default:
          return null
      }
    },
    
    async saveDeviceFields () {
      if (this.savingDevice) return
      this.savingDevice = true
      try {
        const input = { enabled: this.deviceForm.enabled }
        if (this.deviceForm.type) {
          input.type = this.deviceForm.type
        }
        if (this.deviceForm.elevation !== null && this.deviceForm.elevation !== '') {
          input.elevation = parseFloat(this.deviceForm.elevation)
        }
        if (this.deviceForm.latitude !== null && this.deviceForm.latitude !== '') {
          input.latitude = parseFloat(this.deviceForm.latitude)
        }
        if (this.deviceForm.longitude !== null && this.deviceForm.longitude !== '') {
          input.longitude = parseFloat(this.deviceForm.longitude)
        }
        if (this.deviceForm.siteId) {
          input.site = { connect: { id: this.deviceForm.siteId } }
        }
        await this.$apollo.mutate({
          mutation: UPDATE_DEVICE_MUTATION,
          variables: { id: this.device.id, input }
        })
        this.$bvToast.toast('Device updated successfully.', {
          title: 'Saved',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        })
        this.$emit('updated')
      } catch (error) {
        this.$bvToast.toast('Failed to update device. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.savingDevice = false
      }
    },

    async particleApi (method, path, body) {
      var opts = {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + getAccessToken()
        }
      }
      if (body) opts.body = JSON.stringify(body)
      var res = await fetch(API_BASE + path, opts)
      if (!res.ok) {
        var errMsg = 'HTTP ' + res.status
        try {
          var json = await res.json()
          if (json && json.error) errMsg = json.error
        } catch (e) { /* ignore */ }
        throw new Error(errMsg)
      }
      return res.json()
    },

    async findParticleDevice () {
      var serial = (this.device.serial || '').toLowerCase()
      var matchFn = function (pd) {
        return (pd.serialNumber && pd.serialNumber.toLowerCase() === serial) ||
               (pd.particleId && pd.particleId.toLowerCase() === serial) ||
               (pd.name && pd.name.toLowerCase() === serial)
      }
      var devices = await this.particleApi('GET', '/api/particle/devices')
      var found = (devices || []).find(matchFn) || null
      if (!found) {
        await this.particleApi('POST', '/api/particle/sync')
        var synced = await this.particleApi('GET', '/api/particle/devices')
        found = (synced || []).find(matchFn) || null
      }
      return found
    },

    async saveConfiguration () {
      if (!this.isFormValid || this.saving || !this.deviceConfig) return

      this.saving = true
      this.progressModal = { show: true, done: false, value: 15, stage: 'Saving configuration...', variant: 'primary', warning: null }

      try {
        const input = {}
        Object.keys(this.form).forEach(key => {
          const value = this.form[key]
          if (typeof value === 'boolean') {
            input[key] = value
          } else if (value !== null && value !== undefined && value !== '') {
            input[key] = value
          }
        })

        await this.$apollo.mutate({
          mutation: UPDATE_DEVICE_CONFIG_MUTATION,
          variables: { id: this.deviceConfig.id, input }
        })

        this.progressModal.value = 40
        this.progressModal.stage = 'Locating device in Particle Cloud...'

        try {
          var particleDevice = await this.findParticleDevice()

          if (!particleDevice) {
            this.progressModal.done = true
            this.progressModal.variant = 'warning'
            this.progressModal.stage = 'Configuration saved. Device not found in Particle Cloud.'
            this.progressModal.warning = 'Configuration was saved, but the device could not be located in Particle Cloud to send a refresh command.'
            this.$emit('updated')
            return
          }

          this.progressModal.value = 65
          this.progressModal.stage = 'Sending refreshConfig to device (waiting up to 10s)...'

          var timeoutPromise = new Promise(function (_, reject) {
            setTimeout(function () { reject(new Error('No response from device after 10 seconds')) }, 10000)
          })

          var result = await Promise.race([
            this.particleApi('POST', '/api/particle/devices/' + particleDevice.id + '/commands', {
              commandName: 'refreshConfig',
              argument: '',
              useCorrelation: false
            }),
            timeoutPromise
          ])

          var cmdStatus = result.command && result.command.status
          if (cmdStatus === 'failed') {
            this.progressModal.done = true
            this.progressModal.variant = 'warning'
            this.progressModal.stage = 'Configuration saved. Device reported a failure.'
            this.progressModal.warning = 'The refreshConfig command was sent but the device reported a failure. The configuration will apply on next restart.'
            this.$emit('updated')
          } else {
            this.progressModal.value = 100
            this.progressModal.done = true
            this.progressModal.variant = 'success'
            this.$emit('updated')
            var self = this
            setTimeout(function () {
              self.progressModal.show = false
              self.$emit('close')
            }, 1500)
          }
        } catch (particleErr) {
          this.progressModal.done = true
          this.progressModal.variant = 'warning'
          this.progressModal.stage = 'Configuration saved. Refresh command failed.'
          this.progressModal.warning = particleErr.message
          this.$emit('updated')
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating device config:', error)
        this.progressModal.show = false
        this.$bvToast.toast('Failed to update device configuration. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.saving = false
      }
    },
    
    editSensor (sensor) {
      this.$emit('close')
      this.$router.push({ name: 'Sensor Edit', params: { sensorId: sensor.id } })
    },

    calibrateSensor (sensor) {
      this.$emit('close')
      this.$router.push({ name: 'Sensor Calibration', params: { sensorId: sensor.id } })
    },
    
    async deleteSensor (sensor) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete sensor "${sensor.label}"?`,
        {
          title: 'Confirm Deletion',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel'
        }
      )
      
      if (confirmed) {
        try {
          await this.$apollo.mutate({
            mutation: DELETE_DEVICE_SENSOR_MUTATION,
            variables: { id: sensor.id }
          })
          
          // Remove from local array
          const index = this.deviceConfig.sensors.findIndex(s => s.id === sensor.id)
          if (index > -1) {
            this.deviceConfig.sensors.splice(index, 1)
          }
          
          this.$bvToast.toast('Sensor deleted successfully!', {
            title: 'Success',
            variant: 'success',
            solid: true
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error deleting sensor:', error)
          this.$bvToast.toast('Failed to delete sensor. Please try again.', {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
        }
      }
    },
    
    async createNewConfiguration () {
      this.saving = true
      
      try {
        // Create a basic configuration with required fields
        const input = {
          device: { connect: { serial: this.device.serial } },
          type: this.form.type || this.device.type || 'well',
          company: this.form.company,
          loopInt: this.form.loopInt,
          publishInt: this.form.publishInt,
          keepAlive: this.form.keepAlive,
          stayAwake: this.form.stayAwake,
          sleepType: this.form.sleepType || undefined,
          debug: this.form.debug,
          bmeCal: this.form.bmeCal,
          minChgTmp: this.form.minChgTmp,
          voltLimit: this.form.voltLimit,
          currentLimit: this.form.currentLimit,
          chargeVolt: this.form.chargeVolt,
          adcMin: this.form.adcMin,
          adcMax: this.form.adcMax
        }
        
        const result = await this.$apollo.mutate({
          mutation: CREATE_DEVICE_CONFIG_MUTATION,
          variables: { input }
        })
        
        this.deviceConfig = result.data.createDeviceConfig
        
        // Populate form with the new configuration
        Object.keys(this.form).forEach(key => {
          if (this.deviceConfig[key] !== null && this.deviceConfig[key] !== undefined) {
            this.form[key] = this.deviceConfig[key]
          }
        })
        
        this.$bvToast.toast('Device configuration created successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
        
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating device config:', error)
        this.$bvToast.toast('Failed to create device configuration. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.saving = false
      }
    },
    
    async addNewSensor (bvModalEvt) {
      if (bvModalEvt) bvModalEvt.preventDefault()
      if (this.addingSensor) return

      if (!this.newSensor.label || !this.newSensor.type || !this.newSensor.iface) {
        this.$bvToast.toast('Label, type, and interface are required.', {
          title: 'Missing Fields', variant: 'warning', solid: true
        })
        return
      }

      if (!this.deviceConfig || !this.deviceConfig.id) {
        this.$bvToast.toast('Device configuration must be saved before adding sensors.', {
          title: 'No Configuration', variant: 'warning', solid: true
        })
        return
      }

      this.addingSensor = true
      try {
        const sensorInput = {
          label: this.newSensor.label,
          type: this.newSensor.type,
          unit: this.newSensor.unit || '',
          iface: this.newSensor.iface,
          channel: this.newSensor.channel != null ? this.newSensor.channel : 0,
          min: this.newSensor.min != null ? this.newSensor.min : 0,
          max: this.newSensor.max != null ? this.newSensor.max : 100,
          cal: this.newSensor.cal != null ? this.newSensor.cal : 0,
          nr: this.newSensor.nr != null ? this.newSensor.nr : 1
        }

        const result = await this.$apollo.mutate({
          mutation: CREATE_DEVICE_SENSOR_MUTATION,
          variables: {
            configId: this.deviceConfig.id,
            input: sensorInput
          }
        })

        const created = result.data && result.data.createDeviceSensor
        if (created) {
          if (!this.deviceConfig.sensors) this.deviceConfig.sensors = []
          this.deviceConfig.sensors.push(created)
        }

        this.resetNewSensorForm()
        this.showAddSensorModal = false

        this.$bvToast.toast('Sensor added successfully.', {
          title: 'Sensor Added', variant: 'success', solid: true, autoHideDelay: 3000
        })
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error adding sensor:', err)
        this.$bvToast.toast(err.message || 'Failed to add sensor. Please try again.', {
          title: 'Error', variant: 'danger', solid: true
        })
      } finally {
        this.addingSensor = false
      }
    },
    
    cancelAddSensor () {
      this.resetNewSensorForm()
      this.showAddSensorModal = false
    },
    
    resetNewSensorForm () {
      this.newSensor = {
        label: '',
        type: '',
        unit: '',
        iface: '',
        channel: 0,
        min: 0,
        max: 100,
        cal: 1.0,
        nr: 1
      }
    }
  }
}
</script>

<style scoped>
.card-title {
  margin-bottom: 1rem;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}
</style>
